import { z } from "zod";
import { useDynamicConfig } from "../../../../lib/delivery";
import { DynamicConfigs } from "../../../../lib/delivery_keys";
import { useLocalStorageState } from "../../../../hooks/useLocalStorageState";

const StickyAlertConfigSchema = z.object({
  key: z.string().default(""),
  title: z.string().default(""),
  description: z.string().default(""),
  severity: z.enum(["info", "warning", "error"]).default("info"),
  dismissable: z.enum(["NO", "EACH_TIME", "ONCE_FOR_ALL"]).default("NO"),
});

export type StickyAlertConfig = z.infer<typeof StickyAlertConfigSchema>;
export type AlertSeverity = StickyAlertConfig["severity"];
export type AlertDismissMode = StickyAlertConfig["dismissable"];

export interface StickyAlertState {
  /** null when no alert is active or the current one has been dismissed. */
  alert: StickyAlertConfig | null;
  dismiss: () => void;
}

const POLL_MS = 5 * 60 * 1000;

/** Manages the `platform_alert` Statsig dynamic config and per-key dismissal state. */
export function useStickyAlert(defaultConfig?: StickyAlertConfig): StickyAlertState {
  const config =
    useDynamicConfig(DynamicConfigs.platformAlert, StickyAlertConfigSchema, POLL_MS) ??
    defaultConfig ??
    null;

  // One boolean entry per alert key — resets automatically when the PM rotates the key.
  const alertDismissedKey = config?.key ? `rc_alert_dismissed_${config.key}` : null;
  const [dismissed, setDismissed] = useLocalStorageState<boolean>(alertDismissedKey);

  const isActive = config !== null && config.key !== "" && !dismissed;

  function dismiss() {
    if (!config) return;
    setDismissed(true);
  }

  return { alert: isActive ? config : null, dismiss };
}
